import { ErrorBoundary } from '../shared/ErrorBoundary';
import { useNews } from '../../hooks/useNews';
import { useOppositionNews } from '../../hooks/useOppositionNews';
import { format, parseISO } from 'date-fns';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import { useUserProfile } from '../../hooks/useUserProfile';
import { useNextFixture } from '../../hooks/useNextFixture';

interface NewsPanelProps {
  vertical?: boolean;
}

export const NewsPanel = ({ vertical = false }: NewsPanelProps) => {
  const { profile } = useUserProfile();
  const clubId = profile?.clubName || '631'; // Default to Chelsea (631) if no club is selected
  const { news, isLoading, error } = useNews(clubId);
  const { news: oppositionNews, isLoading: oppositionLoading, error: oppositionError } = useOppositionNews();
  const { fixture } = useNextFixture();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [oppositionCurrentIndex, setOppositionCurrentIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<'club' | 'opposition'>('club');

  // Get opposition team name for the panel title
  const oppositionName = fixture ? 
    (fixture.fixture_type === 'home' ? fixture.away_team_name : fixture.home_team_name) : 
    'Opposition';

  useEffect(() => {
    if (!news || news.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((current) => (current + 1) % Math.min(news.length, 5));
    }, 2500); // 2 seconds display + 0.5 seconds transition

    return () => clearInterval(interval);
  }, [news]);

  useEffect(() => {
    if (!oppositionNews || oppositionNews.length === 0) return;

    const interval = setInterval(() => {
      setOppositionCurrentIndex((current) => (current + 1) % Math.min(oppositionNews.length, 5));
    }, 2500); // 2 seconds display + 0.5 seconds transition

    return () => clearInterval(interval);
  }, [oppositionNews]);

  // Format the publication date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'Recent';
    }
    
    try {
      return format(parseISO(dateString), 'MMM d, h:mma');
    } catch (e) {
      console.error('Error parsing date:', dateString, e);
      return 'Recent';
    }
  };

  return (
    <ErrorBoundary>
      <div className="bg-[#121212] rounded-lg shadow-lg p-4 transition-colors h-[50vh] overflow-auto">
        <h2 className="text-xl font-bold mb-2 text-[#CECFD2]">News</h2>
        
        {/* Tabs */}
        <div className="flex border-b border-gray-700 mb-4">
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'club'
                ? 'text-[#4E46FF] border-b-2 border-[#4E46FF]'
                : 'text-[#CECFD2] hover:text-[#4E46FF]'
            }`}
            onClick={() => setActiveTab('club')}
          >
            My Club
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === 'opposition'
                ? 'text-[#4E46FF] border-b-2 border-[#4E46FF]'
                : 'text-[#CECFD2] hover:text-[#4E46FF]'
            }`}
            onClick={() => setActiveTab('opposition')}
          >
            {oppositionName}
          </button>
        </div>
        
        {/* My Club News Tab Content */}
        {activeTab === 'club' && (
          <>
            {isLoading && (
              <div className="flex justify-center items-center h-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#4E46FF]" />
              </div>
            )}

            {error && (
              <div className="text-red-500 text-sm p-2 text-center">
                {error}
              </div>
            )}

            {!isLoading && !error && news && news.length > 0 && (
              vertical ? (
                // Vertical layout - show multiple news items as cards
                <div className="space-y-4">
                  {news.slice(0, 8).map((item) => (
                    <a
                      key={item.id}
                      href={item.article_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group block border border-gray-500 hover:bg-[#26222E] p-3 rounded-lg transition-colors"
                    >
                      <div className="flex items-start space-x-2">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-[#CECFD2] group-hover:text-[#4E46FF] line-clamp-2">
                            {item.headline}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(item.publication_date)}
                            {item.source_name && ` • ${item.source_name}`}
                          </p>
                          <p className="text-xs text-[#CECFD2] mt-2 line-clamp-2">
                            {item.snippet}
                          </p>
                          {item.mentioned_entities && item.mentioned_entities.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {item.mentioned_entities.slice(0, 3).map((entity, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#26222E] text-[#CECFD2]">
                                  {typeof entity === 'string' ? entity : entity?.name || 'Unknown'}
                                </span>
                              ))}
                              {item.mentioned_entities.length > 3 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#26222E] text-[#CECFD2]">
                                  +{item.mentioned_entities.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 text-[#CECFD2] group-hover:text-[#4E46FF] flex-shrink-0 mt-1" />
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                // Horizontal layout (original carousel style)
                <div className="relative h-12 overflow-hidden">
                  {news.slice(0, 5).map((item, index) => (
                    <div
                      key={item.id}
                      className={`absolute w-full transition-all duration-500 ${
                        index === currentIndex 
                          ? 'translate-y-0 opacity-100' 
                          : index < currentIndex
                          ? '-translate-y-full opacity-0'
                          : 'translate-y-full opacity-0'
                      }`}
                    >
                      <a
                        href={item.article_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-start space-x-2 border border-gray-500 hover:bg-[#26222E] p-2 rounded-lg"
                      >
                        <div className="flex-1 truncate">
                          <h3 className="text-sm font-medium text-[#CECFD2] group-hover:text-[#4E46FF] truncate">
                            {item.headline}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {formatDate(item.publication_date)}
                            {item.source_name && ` • ${item.source_name}`}
                          </p>
                        </div>
                        <ArrowTopRightOnSquareIcon className="h-4 w-4 text-[#CECFD2] group-hover:text-[#4E46FF] flex-shrink-0" />
                      </a>

                      {/* Tooltip */}
                      <div className="absolute left-0 right-0 top-full mt-2 hidden group-hover:block z-10">
                        <div className="bg-[#26222E] text-[#CECFD2] text-sm rounded-md p-2 shadow-lg max-w-xs">
                          {item.snippet}
                          {item.mentioned_entities && item.mentioned_entities.length > 0 && (
                            <div className="mt-2">
                              <span className="text-xs font-semibold">Mentioned: </span>
                              <span className="text-xs">
                                {item.mentioned_entities.map(entity => 
                                  typeof entity === 'string' ? entity : entity?.name || 'Unknown'
                                ).join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {!isLoading && !error && (!news || news.length === 0) && (
              <div className="h-12 flex items-center justify-center">
                <p className="text-[#CECFD2] text-sm">No club news available</p>
              </div>
            )}
          </>
        )}
        
        {/* Opposition News Tab Content */}
        {activeTab === 'opposition' && (
          <>
            {oppositionLoading && (
              <div className="flex justify-center items-center h-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#4E46FF]" />
              </div>
            )}

            {oppositionError && (
              <div className="text-red-500 text-sm p-2 text-center">
                {oppositionError}
              </div>
            )}

            {!oppositionLoading && !oppositionError && oppositionNews && oppositionNews.length > 0 && (
              <div className="space-y-4">
                {oppositionNews.slice(0, 8).map((item) => (
                  <a
                    key={item.id}
                    href={item.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block border border-gray-500 hover:bg-[#26222E] p-3 rounded-lg transition-colors"
                  >
                    <div className="flex items-start space-x-2">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-[#CECFD2] group-hover:text-[#4E46FF] line-clamp-2">
                          {item.headline}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {format(parseISO(item.surfacedAt), 'MMM d, h:mma')}
                        </p>
                        <p className="text-xs text-[#CECFD2] mt-2 line-clamp-2">
                          {item.summary}
                        </p>
                      </div>
                      <ArrowTopRightOnSquareIcon className="h-4 w-4 text-[#CECFD2] group-hover:text-[#4E46FF] flex-shrink-0 mt-1" />
                    </div>
                  </a>
                ))}
              </div>
            )}

            {!oppositionLoading && !oppositionError && (!oppositionNews || oppositionNews.length === 0) && (
              <div className="h-12 flex items-center justify-center">
                <p className="text-[#CECFD2] text-sm">No {oppositionName} news available</p>
              </div>
            )}
          </>
        )}
      </div>
    </ErrorBoundary>
  );
};